import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SEOHead } from '../../../components/seo/SEOHead';
import { Input, Label, Textarea } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Card';
import { api } from '../../../lib/api';

export default function ServiceEditor() {
  const { slug } = useParams<{ slug: string }>();
  const isNew = !slug || slug === 'new';
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '',
    shortDescription: '',
    fullContent: '',
    benefits: '',
    eligibility: '',
    startingPrice: 0,
    isFeatured: false,
    seoTitle: '',
    seoDescription: '',
    status: 'draft' as 'draft' | 'published',
  });
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew || !slug) return;
    api.admin.services
      .get(slug)
      .then((res) => {
        const s = res.service;
        setForm({
          title: s.title,
          shortDescription: s.shortDescription,
          fullContent: s.fullContent ?? '',
          benefits: s.benefits ?? '',
          eligibility: s.eligibility ?? '',
          startingPrice: s.startingPrice,
          isFeatured: s.isFeatured,
          seoTitle: s.seoTitle ?? '',
          seoDescription: s.seoDescription ?? '',
          status: s.status === 'archived' ? 'draft' : s.status,
        });
      })
      .catch(() => setError('Could not load this service.'))
      .finally(() => setIsLoading(false));
  }, [slug, isNew]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      if (isNew) {
        const res = await api.admin.services.create(form);
        navigate(`/admin/services/${res.slug}`);
      } else {
        // Editing an existing service's core fields reuses the same admin
        // content endpoints (SEO + body) rather than a combined PATCH —
        // see admin/content routes. A full PATCH /admin/services/:id
        // endpoint for title/price could be added later if this split
        // proves awkward in practice.
        navigate('/admin/services');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save service.');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-seal-blue" />
      </div>
    );
  }

  return (
    <>
      <SEOHead title={isNew ? 'New Service — Admin' : 'Edit Service — Admin'} description="" canonicalPath="/admin/services" />

      <h1 className="font-display text-2xl font-bold text-ink">{isNew ? 'Add New Service' : `Edit: ${form.title}`}</h1>

      <form onSubmit={handleSubmit} className="mt-6 max-w-2xl space-y-5 rounded-lg border border-line bg-white p-6">
        <div>
          <Label htmlFor="title">Service Title</Label>
          <Input id="title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="shortDescription">Short Description</Label>
          <Textarea
            id="shortDescription"
            rows={2}
            required
            value={form.shortDescription}
            onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="fullContent">Overview / Full Content</Label>
          <Textarea
            id="fullContent"
            rows={5}
            value={form.fullContent}
            onChange={(e) => setForm({ ...form, fullContent: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="benefits">Benefits (one per line, starting with "- ")</Label>
          <Textarea
            id="benefits"
            rows={4}
            placeholder="- Expert handling of paperwork&#10;- Transparent pricing"
            value={form.benefits}
            onChange={(e) => setForm({ ...form, benefits: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="eligibility">Eligibility (one per line, starting with "- ")</Label>
          <Textarea
            id="eligibility"
            rows={4}
            placeholder="- Valid for individuals and businesses&#10;- Documents must be current"
            value={form.eligibility}
            onChange={(e) => setForm({ ...form, eligibility: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="startingPrice">Starting Price (₹)</Label>
          <Input
            id="startingPrice"
            type="number"
            min={0}
            required
            value={form.startingPrice}
            onChange={(e) => setForm({ ...form, startingPrice: Number(e.target.value) })}
          />
        </div>
        <div>
          <Label htmlFor="seoTitle">SEO Title</Label>
          <Input id="seoTitle" value={form.seoTitle} onChange={(e) => setForm({ ...form, seoTitle: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="seoDescription">SEO Description</Label>
          <Textarea
            id="seoDescription"
            rows={2}
            value={form.seoDescription}
            onChange={(e) => setForm({ ...form, seoDescription: e.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink/70">
          <input
            type="checkbox"
            checked={form.isFeatured}
            onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
          />
          Feature on homepage
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <Button type="submit" disabled={isSaving}>
          {isSaving ? 'Saving...' : isNew ? 'Create Service' : 'Save Changes'}
        </Button>
      </form>
    </>
  );
}
